import { useEffect, useCallback, useState } from 'react';

/**
 * Hook to warn users about unsaved changes when leaving a form
 * 
 * This version works with BrowserRouter (doesn't require data router)
 * Uses window.onbeforeunload for browser close/refresh protection
 * For in-app navigation, components should check hasUnsavedChanges before navigating
 * 
 * Usage:
 * const { hasUnsavedChanges, setHasUnsavedChanges, showDialog, setShowDialog, confirmDiscard } = useFormExitPrompt();
 * // Call setHasUnsavedChanges(true) when form is dirty
 * // Call setHasUnsavedChanges(false) after successful save
 * // Use showDialog to render the FormExitDialog
 * // Call confirmDiscard() to proceed with navigation after discard
 */
export const useFormExitPrompt = (initialState = false) => {
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(initialState);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Wrapper to update state and beforeunload
  const setHasUnsavedChanges = useCallback((hasChanges) => {
    setHasUnsavedChangesState(hasChanges);
    
    if (hasChanges) {
      // Add beforeunload listener for browser close/refresh
      window.onbeforeunload = (e) => {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      };
    } else {
      window.onbeforeunload = null;
    }
  }, []);

  // Function to check before navigating
  const checkBeforeNavigate = useCallback((navigateFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigateFn);
      setShowDialog(true);
      return false; // Prevent navigation
    }
    return true; // Allow navigation
  }, [hasUnsavedChanges]);

  // Function to confirm discard and proceed with pending navigation
  const confirmDiscard = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, setHasUnsavedChanges]);

  // Function to cancel and keep editing
  const cancelDiscard = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  // For backward compatibility, create a blocker-like object
  const blocker = {
    state: showDialog ? 'blocked' : 'unblocked',
    proceed: confirmDiscard,
    reset: cancelDiscard
  };

  return { 
    hasUnsavedChanges, 
    setHasUnsavedChanges, 
    showDialog, 
    setShowDialog,
    checkBeforeNavigate,
    confirmDiscard,
    cancelDiscard,
    blocker // For backward compatibility
  };
};

/**
 * Confirmation dialog for discarding form changes
 */
export const FormExitDialog = ({ 
  isOpen, 
  onClose, 
  onDiscard, 
  onSaveDraft,
  showSaveDraft = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div data-testid="form-exit-dialog" className="relative bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-6 max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-2">
          Unsaved Changes
        </h3>
        <p className="text-[var(--theme-text-secondary)] mb-6">
          You have unsaved changes. What would you like to do?
        </p>
        
        <div className="flex flex-col gap-3">
          {showSaveDraft && (
            <button
              onClick={onSaveDraft}
              data-testid="form-exit-save-draft-btn"
              className="w-full py-2.5 px-4 bg-[#C9956C] hover:bg-[#B8845B] text-white font-medium rounded-lg transition-colors"
            >
              Save as Draft
            </button>
          )}
          <button
            onClick={onDiscard}
            data-testid="form-exit-discard-btn"
            className="w-full py-2.5 px-4 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-medium rounded-lg transition-colors"
          >
            Discard Changes
          </button>
          <button
            onClick={onClose}
            data-testid="form-exit-keep-editing-btn"
            className="w-full py-2.5 px-4 bg-[var(--theme-hover)] hover:bg-[var(--theme-border)] text-[var(--theme-text-primary)] font-medium rounded-lg transition-colors"
          >
            Keep Editing
          </button>
        </div>
      </div>
    </div>
  );
};
